import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  VaButton,
  VaMemorableDate,
  VaModal,
  VaSelect,
  VaTextInput,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { focusElement } from 'platform/utilities/ui';
import { STATUS_FILTERS } from '../utilities/combinedHistoryFilters';
import { isoDateFrom } from '../utilities/combinedHistoryPaging';

/**
 * @typedef {import('../utilities/combinedHistoryFilters').FilterOption} FilterOption
 * @typedef {Pick<import('../utilities/combinedHistoryFilters').HistoryFilters, 'status'|'form'|'date'|'confirmation'>} ModalFilters
 */

// The wording va-memorable-date uses for its own part-filled check on blur,
// so the reader meets one message for the field, whichever check caught it.
const DATE_ERROR = 'Please enter a complete date';

/**
 * @param {string|null|undefined} value What va-memorable-date reports: empty,
 *   `YYYY-M-D` with any part blank while the reader types, or padded once a
 *   field blurs.
 * @returns {string|null|false} The date as `YYYY-MM-DD`, null for nothing
 *   entered, or false for a part-filled or impossible date.
 */
const readEnteredDate = value => {
  if (!value || value === '--') return null;
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return false;
  return isoDateFrom(Number(year), Number(month), Number(day)) ?? false;
};

/**
 * The Filters dialog over the combined history: Status, Submission type,
 * Submission date, and Confirmation number. Every filter is here, under a
 * label, so none of them sits alone where the portal's sort selects do.
 * The fields are edited here and applied together from Apply filters;
 * Clear filters empties them without applying, says so in a slim success
 * alert, and the dialog's close control drops what was edited. The parent mounts the dialog again (a new
 * `key`) each time it opens, so the fields start from the filters in force.
 *
 * Each control is a design system component: va-select for the status and
 * for the submission type (one each, since the design system has no
 * multiselect), va-memorable-date for the date (the design system has no
 * calendar picker), and va-text-input for the confirmation number. The
 * selects carry va-select's `va-sort__select` class, which is how the
 * design system's own va-sort keeps va-select from adding a blank
 * "- Select -" entry, so the "All" entry is the only empty one; the class
 * also gives the label a small top margin from 640px, which
 * is accepted. A part-filled date is refused on Apply with the component's
 * own wording, and focus moves to the field, as the forms pattern does.
 *
 * @param {Object} props
 * @param {boolean} props.visible Whether the dialog is open.
 * @param {ModalFilters} props.filters The filters in force when it opened.
 * @param {FilterOption[]} props.formOptions The Submission type entries.
 * @param {(filters: ModalFilters) => void} props.onApply Called with the
 *   fields as edited when Apply filters is used.
 * @param {() => void} props.onClose Called when the dialog closes without
 *   applying.
 * @returns {JSX.Element}
 */
const CombinedHistoryFiltersModal = ({
  visible,
  filters,
  formOptions,
  onApply,
  onClose,
}) => {
  const [draft, setDraft] = useState({
    status: filters.status,
    form: filters.form,
    date: filters.date ?? '',
    confirmation: filters.confirmation ?? '',
  });
  const [dateError, setDateError] = useState(null);
  // Clear empties fields that may already look empty, so a slim alert says
  // it happened, for sighted readers and, through its live region, for
  // screen readers. The dialog mounts again each time it opens, so the
  // alert starts hidden; the next edit hides it again.
  const [cleared, setCleared] = useState(false);
  const dateRef = useRef(null);

  const change = patch => {
    setCleared(false);
    setDraft(current => ({ ...current, ...patch }));
  };
  // A refused date's message goes once the date is touched, not only on the
  // next Apply: `error` is passed on every render and would put it back.
  const changeDate = date => {
    setDateError(null);
    change({ date });
  };

  const clear = () => {
    setDateError(null);
    setDraft({ status: null, form: null, date: '', confirmation: '' });
    setCleared(true);
  };

  const apply = () => {
    const date = readEnteredDate(draft.date);
    if (date === false) {
      setDateError(DATE_ERROR);
      focusElement(dateRef.current);
      return;
    }
    setDateError(null);
    onApply({
      status: draft.status,
      form: draft.form,
      date,
      confirmation: draft.confirmation.trim() || null,
    });
  };

  return (
    <VaModal
      data-testid="combined-history-filters-modal"
      modalTitle="Filters"
      large
      visible={visible}
      onCloseEvent={onClose}
    >
      {/* va-modal lays its content out as a flex item sized to its content,
          so the controls would come out as wide as the widest one; the
          wrapper's width lets them fill the dialog. See SubmissionsPage.scss. */}
      <div className="submissions__history-filters-modal">
        <VaSelect
          class="va-sort__select vads-u-display--block"
          data-testid="combined-history-status-filter"
          label="Status"
          name="status"
          fullWidth
          value={draft.status ?? ''}
          onVaSelect={event => change({ status: event.detail.value || null })}
        >
          <option value="">All statuses</option>
          {STATUS_FILTERS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </VaSelect>
        <VaSelect
          class="va-sort__select vads-u-display--block vads-u-margin-top--2"
          data-testid="combined-history-form-filter"
          label="Submission type"
          name="form"
          fullWidth
          value={draft.form ?? ''}
          onVaSelect={event => change({ form: event.detail.value || null })}
        >
          <option value="">All types</option>
          {formOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </VaSelect>
        <VaMemorableDate
          ref={dateRef}
          data-testid="combined-history-date-filter"
          class="vads-u-margin-top--2"
          label="Submission date"
          name="date"
          value={draft.date}
          error={dateError}
          onDateChange={event => changeDate(event.target.value ?? '')}
          onDateBlur={event => changeDate(event.target.value ?? '')}
        />
        <VaTextInput
          data-testid="combined-history-confirmation-filter"
          class="vads-u-margin-top--2"
          label="Confirmation number"
          name="confirmation"
          value={draft.confirmation}
          onInput={event => change({ confirmation: event.target.value ?? '' })}
        />
        {/* The live region is in the DOM from the first paint, so screen
            readers already watch it when the alert mounts inside it. */}
        <div
          aria-live="polite"
          aria-relevant="all"
          data-testid="combined-history-filters-status"
        >
          {cleared && (
            <va-alert
              slim
              status="success"
              class="vads-u-margin-top--4"
              data-testid="combined-history-filters-cleared"
            >
              <p className="vads-u-margin-y--0">Filters cleared</p>
            </va-alert>
          )}
        </div>
        <div className="vads-u-display--flex vads-u-justify-content--flex-end vads-u-margin-top--4">
          <VaButton
            secondary
            class="vads-u-margin-right--2"
            data-testid="combined-history-filters-clear"
            text="Clear filters"
            onClick={clear}
          />
          <VaButton
            data-testid="combined-history-filters-apply"
            text="Apply filters"
            onClick={apply}
          />
        </div>
      </div>
    </VaModal>
  );
};

const optionType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

CombinedHistoryFiltersModal.propTypes = {
  filters: PropTypes.shape({
    status: PropTypes.string,
    form: PropTypes.string,
    date: PropTypes.string,
    confirmation: PropTypes.string,
  }).isRequired,
  formOptions: PropTypes.arrayOf(optionType).isRequired,
  visible: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CombinedHistoryFiltersModal;
